import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '@/utils/store';
import { useAuth } from '@/context/AuthContext';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { startRealtimeSync } from '@/lib/sync-engine';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { Heart, Copy, Check } from 'lucide-react';

export default function CoupleConnect() {
  const { t } = useTranslation();
  const { refreshProfile } = useAuth();

  const [coupleCode, setCoupleCode] = useState(null);
  const [inputCode, setInputCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleGenerate = async () => {
    if (!isSupabaseConfigured()) return;
    setLoading(true);
    setError('');

    try {
      const { data, error: rpcError } = await supabase.rpc('generate_couple_invite');
      if (rpcError) throw rpcError;
      setCoupleCode(data.code);
    } catch (err) {
      setError(err.message || t('couple.connect.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(coupleCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleConnect = async () => {
    if (inputCode.length !== 6) {
      setError(t('couple.connect.invalidCode'));
      return;
    }

    if (!isSupabaseConfigured()) return;
    setLoading(true);
    setError('');

    try {
      const { data, error: rpcError } = await supabase.rpc('join_couple', {
        p_code: inputCode,
      });
      if (rpcError) throw rpcError;

      // Refresh auth profile to get couple_id
      await refreshProfile();

      // Start realtime sync for couple data
      await startRealtimeSync();

      // Update store to reflect coupled state
      useStore.setState({ _fromRemote: true, isCoupled: true });
    } catch (err) {
      setError(err.message || t('couple.connect.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="text-center py-8">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-pink-500 to-rose-600 mx-auto mb-4 flex items-center justify-center">
          <Heart size={36} className="text-white" />
        </div>
        <h1 className="text-2xl font-display font-bold text-gray-900 dark:text-white mb-2">
          {t('couple.connect.title')}
        </h1>
        <p className="text-sm text-gray-500 max-w-xs mx-auto">
          {t('couple.connect.description')}
        </p>
      </div>

      {/* Generate code */}
      <div className="card space-y-4">
        <h2 className="font-semibold text-gray-900 dark:text-white">
          {t('couple.connect.shareCode')}
        </h2>
        {coupleCode ? (
          <div className="flex items-center gap-3">
            <div className="flex-1 bg-gray-100 dark:bg-gray-800 rounded-xl py-4 text-center font-mono text-2xl font-bold tracking-[0.5em] text-gray-900 dark:text-white">
              {coupleCode}
            </div>
            <button
              onClick={handleCopy}
              className="p-3 rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              {copied ? <Check size={20} className="text-success-500" /> : <Copy size={20} className="text-gray-500" />}
            </button>
          </div>
        ) : (
          <Button className="w-full" onClick={handleGenerate} loading={loading}>
            {t('couple.connect.generate')}
          </Button>
        )}
      </div>

      {/* Enter partner code */}
      <div className="card space-y-4">
        <h2 className="font-semibold text-gray-900 dark:text-white">
          {t('couple.connect.enterCode')}
        </h2>
        {error && (
          <div className="p-3 rounded-xl bg-danger-50 dark:bg-danger-500/10 text-danger-700 dark:text-danger-400 text-sm">
            {error}
          </div>
        )}
        <Input
          placeholder="000000"
          value={inputCode}
          onChange={(e) => setInputCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          className="text-center font-mono text-xl"
        />
        <Button className="w-full" variant="secondary" onClick={handleConnect} loading={loading}>
          {t('couple.connect.join')}
        </Button>
      </div>
    </div>
  );
}
